import React, { useMemo, useState } from 'react';
import type { CRMUser, ProjectTask } from '../../types';
import StatusPill from './StatusPill';
import PriorityBadge from './PriorityBadge';
import StructureMultiSelect from './StructureMultiSelect';
import { Check, ChevronDown, ChevronRight, ExternalLink, Plus, Trash2 } from 'lucide-react';
import type { CommitTaskDatesHint } from '../../utils/taskDateCommit';
import { toLocalDateInputValue } from '../../utils/taskSchedule';
import {
  formatSubtaskStructurePill,
  formatSubtaskStructuresTitle,
  getSubtaskStructures,
  subtaskStructurePayload,
  type SubtaskStructureRef,
} from '../../utils/subtaskStructures';
import TaskSupplierSelect, { type TaskSupplierOption } from './TaskSupplierSelect';
import './TaskSubtaskSection.css';

export type CreateSubtaskOptions = {
  structures?: SubtaskStructureRef[];
};

interface TaskSubtaskSectionProps {
  parentTaskId: string;
  childTasks: ProjectTask[];
  dealId?: string | null;
  teamMembers: CRMUser[];
  onUpdate: (id: string, updates: Partial<ProjectTask>) => void;
  onDelete: (id: string) => void;
  onCreateSubtask: (parentId: string, title: string, opts?: CreateSubtaskOptions) => void | Promise<void>;
  onSelectSubtask?: (task: ProjectTask) => void;
  onCommitTaskDates?: (
    taskId: string,
    startDate: Date,
    endDate: Date,
    hint: CommitTaskDatesHint,
  ) => void | Promise<void>;
  supplierOptions?: TaskSupplierOption[];
  showSupplierField?: boolean;
  derivedSupplierLabelForTask?: (task: ProjectTask) => string | null;
}

const TaskSubtaskSection: React.FC<TaskSubtaskSectionProps> = ({
  parentTaskId,
  childTasks,
  dealId,
  teamMembers,
  onUpdate,
  onDelete,
  onCreateSubtask,
  onSelectSubtask,
  onCommitTaskDates,
  supplierOptions = [],
  showSupplierField = false,
  derivedSupplierLabelForTask,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [newTitle, setNewTitle] = useState('');
  const [newStructures, setNewStructures] = useState<SubtaskStructureRef[]>([]);
  const [adding, setAdding] = useState(false);

  const sortedTasks = useMemo(
    () => [...childTasks].sort((a, b) => (a.position || 0) - (b.position || 0)),
    [childTasks],
  );
  const doneCount = childTasks.filter((t) => t.status === 'completed').length;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleComplete = (child: ProjectTask) => {
    if (!child.id) return;
    const markDone = child.status !== 'completed';
    onUpdate(child.id, {
      status: markDone ? 'completed' : 'pending',
      progress: markDone ? 100 : Math.min(child.progress, 99),
    });
  };

  const handleAdd = async () => {
    const title = newTitle.trim();
    if (!title || adding) return;
    setAdding(true);
    try {
      await onCreateSubtask(parentTaskId, title, {
        structures: newStructures.length ? newStructures : undefined,
      });
      setNewTitle('');
      setNewStructures([]);
    } catch (err) {
      console.error('Create subtask failed:', err);
      window.alert(err instanceof Error ? err.message : 'Could not create subtask. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const renderExpandedDetails = (child: ProjectTask) => {
    if (!child.id) return null;
    const startVal = child.startDate ? toLocalDateInputValue(new Date(child.startDate)) : '';
    const endVal = child.endDate ? toLocalDateInputValue(new Date(child.endDate)) : '';

    return (
      <div className="pm-subtask-expanded" onClick={(e) => e.stopPropagation()}>
        <div className="pm-subtask-expanded-row">
          <label className="pm-subtask-expanded-label">Title</label>
          <input
            className="pm-inline-input"
            value={child.title}
            onChange={(e) => onUpdate(child.id!, { title: e.target.value })}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== child.title) onUpdate(child.id!, { title: v });
            }}
          />
        </div>
        <div className="pm-subtask-expanded-row">
          <label className="pm-subtask-expanded-label">Linked structures</label>
          <StructureMultiSelect
            dealId={dealId}
            value={getSubtaskStructures(child)}
            placeholder="Select structures…"
            onChange={(structures) => {
              onUpdate(child.id!, subtaskStructurePayload(structures));
            }}
          />
        </div>
        {showSupplierField && (
          <div className="pm-subtask-expanded-row">
            <label className="pm-subtask-expanded-label">Supplier</label>
            <TaskSupplierSelect
              options={supplierOptions}
              value={child.supplierOrgIds ?? []}
              derivedLabel={derivedSupplierLabelForTask?.(child) ?? null}
              onChange={(orgIds) => onUpdate(child.id!, { supplierOrgIds: orgIds, supplierManual: true })}
            />
          </div>
        )}
        <div className="pm-subtask-expanded-row pm-subtask-expanded-row--split">
          <div>
            <label className="pm-subtask-expanded-label">Status</label>
            <StatusPill
              status={child.status}
              onChange={(s) =>
                onUpdate(child.id!, {
                  status: s,
                  progress: s === 'completed' ? 100 : child.progress,
                })
              }
            />
          </div>
          <div>
            <label className="pm-subtask-expanded-label">Priority</label>
            <PriorityBadge
              priority={child.priority}
              onChange={(p) => onUpdate(child.id!, { priority: p })}
            />
          </div>
        </div>
        <div className="pm-subtask-expanded-row pm-subtask-expanded-row--split">
          <div>
            <label className="pm-subtask-expanded-label">Start</label>
            <input
              type="date"
              className="pm-inline-input pm-subtask-date-input"
              value={startVal}
              onChange={(e) => {
                const start = e.target.value
                  ? new Date(`${e.target.value}T12:00:00`)
                  : new Date(child.startDate);
                const end = new Date(child.endDate);
                if (onCommitTaskDates) {
                  void onCommitTaskDates(child.id!, start, end, { fromTaskPanel: true });
                } else {
                  onUpdate(child.id!, { startDate: start });
                }
              }}
            />
          </div>
          <div>
            <label className="pm-subtask-expanded-label">Due</label>
            <input
              type="date"
              className="pm-inline-input pm-subtask-date-input"
              value={endVal}
              onChange={(e) => {
                const end = e.target.value
                  ? new Date(`${e.target.value}T12:00:00`)
                  : new Date(child.endDate);
                const start = new Date(child.startDate);
                if (onCommitTaskDates) {
                  void onCommitTaskDates(child.id!, start, end, { fromTaskPanel: true });
                } else {
                  onUpdate(child.id!, { endDate: end });
                }
              }}
            />
          </div>
        </div>
        <div className="pm-subtask-expanded-row">
          <label className="pm-subtask-expanded-label">Assignee</label>
          <select
            className="pm-inline-select pm-subtask-assignee-select"
            value={child.assignedTo[0] || ''}
            onChange={(e) => {
              const uid = e.target.value;
              onUpdate(child.id!, { assignedTo: uid ? [uid] : [] });
            }}
          >
            <option value="">Unassigned</option>
            {teamMembers.map((u) => (
              <option key={u.uid} value={u.uid}>
                {u.displayName}
              </option>
            ))}
          </select>
        </div>
        {onSelectSubtask && (
          <button
            type="button"
            className="pm-subtask-open-full"
            onClick={() => onSelectSubtask(child)}
          >
            <ExternalLink size={12} /> Open full task panel
          </button>
        )}
      </div>
    );
  };

  const renderSubtaskRow = (child: ProjectTask) => {
    if (!child.id) return null;
    const expanded = expandedIds.has(child.id);
    const done = child.status === 'completed';
    const pill = formatSubtaskStructurePill(child);
    const pillTitle = formatSubtaskStructuresTitle(child);

    return (
      <div
        key={child.id}
        className={`pm-subtask-card ${done ? 'pm-subtask-card--done' : ''} ${expanded ? 'pm-subtask-card--expanded' : ''}`}
      >
        <div className="pm-subtask-card-header">
          <button
            type="button"
            className="pm-subtask-expand-btn"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse subtask details' : 'Expand subtask details'}
            onClick={() => toggleExpanded(child.id!)}
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <button
            type="button"
            className={`pm-task-check pm-subtask-check ${done ? 'pm-task-checked' : ''}`}
            onClick={() => toggleComplete(child)}
            aria-label={done ? 'Mark incomplete' : 'Mark complete'}
          >
            {done && <Check size={10} strokeWidth={3} />}
          </button>
          <button
            type="button"
            className="pm-subtask-card-title"
            onClick={() => toggleExpanded(child.id!)}
          >
            {child.title}
          </button>
          {!expanded && pill && (
            <span className="pm-subtask-structure-pill" title={pillTitle}>
              {pill}
            </span>
          )}
          {!expanded && (
            <StatusPill status={child.status} readonly />
          )}
          <button
            type="button"
            className="pm-btn-ghost pm-btn-sm pm-subtask-delete-btn"
            title="Delete subtask"
            onClick={() => onDelete(child.id!)}
          >
            <Trash2 size={12} />
          </button>
        </div>
        {expanded && renderExpandedDetails(child)}
      </div>
    );
  };

  return (
    <div className="pm-subtask-section">
      <div className="pm-subtask-section-header">
        <h4 className="pm-detail-section-title">
          Subtasks
          {childTasks.length > 0 && (
            <span className="pm-detail-section-count">
              {doneCount}/{childTasks.length}
            </span>
          )}
        </h4>
      </div>

      {childTasks.length === 0 ? (
        <p className="pm-subtask-empty">No subtasks yet — add one below and assign a structure to group it on the timeline.</p>
      ) : (
        <div className="pm-subtask-list">
          {sortedTasks.map((child) => renderSubtaskRow(child))}
        </div>
      )}

      <div className="pm-subtask-composer">
        <div className="pm-subtask-composer-fields">
          <input
            className="pm-inline-input pm-subtask-composer-title"
            placeholder="New subtask title…"
            value={newTitle}
            disabled={adding}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAdd();
            }}
          />
          <StructureMultiSelect
            dealId={dealId}
            value={newStructures}
            placeholder="Select structures…"
            onChange={setNewStructures}
          />
        </div>
        <div className="pm-subtask-composer-actions">
          <button
            type="button"
            className="pm-btn pm-btn-primary pm-btn-sm pm-subtask-composer-add"
            disabled={!newTitle.trim() || adding}
            title={!newTitle.trim() ? 'Enter a subtask title to enable Add' : undefined}
            aria-disabled={!newTitle.trim() || adding}
            onClick={() => void handleAdd()}
          >
            <Plus size={14} /> Add
          </button>
          {!newTitle.trim() && (
            <span className="pm-subtask-composer-hint">Enter a title to add a subtask</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskSubtaskSection;
